/**
 * Resolve a backend API error into a localized, user-facing message.
 *
 * Backend errors use a structured detail envelope `{ code, message }` (see
 * backend/app/errors.py): `code` is a stable machine string we map to a
 * localized message via `t('errors.<code>')`, and `message` is a Japanese
 * fallback (for logs / non-localized clients) that we deliberately NEVER render
 * — so switching the UI to English localizes these errors instead of leaking
 * Japanese. Legacy string details and unknown codes fall back to a generic
 * localized message (`fallbackKey`).
 *
 * @param {object} error      axios-style error (reads error.response.data.detail)
 * @param {function} t        react-i18next t()
 * @param {string} fallbackKey i18n key used when there is no recognizable code
 * @returns {string} a localized, user-safe message
 */
export function resolveErrorMessage(error, t, fallbackKey = 'common.error') {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && typeof detail.code === 'string') {
    return t(`errors.${detail.code}`, { defaultValue: t(fallbackKey) });
  }
  return t(fallbackKey);
}

/**
 * The raw backend detail string, for NON-rendered uses only — e.g. substring
 * signal detection (a timeout marker). Handles both the structured `{message}`
 * envelope and legacy string details. Returns '' when absent.
 *
 * Do NOT render this in the UI: it is the Japanese fallback message. Use
 * resolveErrorMessage() for anything shown to the user.
 */
export function rawErrorDetail(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message;
  }
  return '';
}
